import React from 'react';
import './Radio.css';

/**
 * Radio Component
 * Standardized radio button group
 * 
 * @param {string} label - Radio group label
 * @param {string} name - Radio group name (required for grouping)
 * @param {Array} options - Array of {value: string, label: string}
 * @param {string} value - Selected value
 * @param {function} onChange - Change handler: (value: string) => void
 * @param {string} error - Error message
 * @param {boolean} disabled - Disabled state
 * @param {boolean} required - Required field indicator
 * @param {string} orientation - Layout orientation: 'horizontal' | 'vertical' (default: 'vertical')
 * @param {string} helperText - Helper text below input
 * @param {string} className - Additional CSS classes
 */
const Radio = ({
  label,
  name,
  options = [],
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  orientation = 'vertical',
  helperText,
  className = ''
}) => {
  const handleChange = (optionValue) => {
    if (!disabled && onChange) {
      onChange(optionValue);
    }
  };

  const orientationClass = orientation === 'horizontal' ? 'radio-horizontal' : 'radio-vertical';
  const groupId = name || `radio-group-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`radio-container ${error ? 'error' : ''} ${className}`.trim()}>
      {label && (
        <label className="radio-label">
          {label}
          {required && <span className="radio-required">*</span>}
        </label>
      )}
      <div className={`radio-group ${orientationClass}`} role="radiogroup" aria-required={required} aria-invalid={error ? 'true' : 'false'}>
        {options.map((option) => {
          const isSelected = value === option.value;
          const optionId = `${groupId}-${option.value}`;

          return (
            <div key={option.value} className="radio-item">
              <input
                type="radio"
                id={optionId}
                name={groupId}
                value={option.value}
                checked={isSelected}
                onChange={() => handleChange(option.value)}
                disabled={disabled}
                className="radio-input"
                aria-describedby={error ? `${groupId}-error` : helperText ? `${groupId}-helper` : undefined}
              />
              <label
                htmlFor={optionId}
                className={`radio-option-label ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
              >
                <span className="radio-custom"></span>
                <span className="radio-option-text">{option.label}</span>
              </label>
            </div>
          );
        })}
      </div>
      {(error || helperText) && (
        <div className="radio-footer">
          {error && (
            <span id={`${groupId}-error`} className="radio-error" role="alert">
              {error}
            </span>
          )}
          {!error && helperText && (
            <span id={`${groupId}-helper`} className="radio-helper">
              {helperText}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default Radio;










